import { useCallback, type RefObject } from 'react'

import { deriveContentState } from './deriveContentState'
import { useGlobalSearchItemActions } from './useGlobalSearchItemActions'
import { useGlobalSearchKeyboardNavigation } from './useGlobalSearchKeyboardNavigation'
import { useGlobalSearchQueryState } from './useGlobalSearchQueryState'
import { useGlobalSearchTabState } from './useGlobalSearchTabState'
import { GlobalSearchUseCaseErrors } from '../../../../business-logic/use-cases/GlobalSearchUseCaseErrors'
import { type GlobalSearchStateMessage } from '../../../../data-access/types/GlobalSearchResponse'
import { useGlobalSearchNavigation } from '../../../../hooks/useGlobalSearchNavigation'
import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { type GlobalSearchResultItem } from '../../../../value-objects/GlobalSearchResultItem'
import { type ContentStateEnum } from '../../enums/ContentStateEnum'
import {
  type GlobalSearchTab,
  type GlobalSearchTabTypeEnum,
} from '../../enums/GlobalSearchTabTypeEnum'
import { useMutateAddGlobalSearchRecentItem } from '../../models/useMutateAddGlobalSearchRecentItem'
import { useQueryGlobalSearchRecentItems } from '../../models/useQueryGlobalSearchRecentItems'
import { useQueryGlobalSearchResults } from '../../models/useQueryGlobalSearchResults'

interface IUseGetGlobalSearchResultsPresenterResult {
  commands: {
    changeQuery: (value: string) => void
    openHighlightedOrSubmit: () => void
    openRecentItem: (item: GlobalSearchRecentItem) => void
    openResultItem: (item: GlobalSearchResultItem) => void
    selectTab: (type: GlobalSearchTabTypeEnum) => void
    navigateDown: () => void
    navigateUp: () => void
  }
  data: {
    query: string
    submittedQuery: string
    recentItems: GlobalSearchRecentItem[]
    resultItems: GlobalSearchResultItem[]
    tabs: Nullable<GlobalSearchTab[]>
    inputRef: RefObject<HTMLInputElement>
    highlightedIndex: Nullable<number>
    activeDescendantId: string | undefined
    isExpanded: boolean
    originalQuery: Nullable<string>
  }
  states: {
    showEnterHint: boolean
    contentState: Nullable<ContentStateEnum>
    selectedTab: GlobalSearchTabTypeEnum
    showTruncatedNotice: boolean
    noResultsMessage: Nullable<GlobalSearchStateMessage>
    unsupportedQueryMessage: Nullable<GlobalSearchStateMessage>
    errorMessage: Nullable<string>
    showLlmConfirmation: boolean
  }
}

export const useGetGlobalSearchResultsPresenter = (
  onClose: () => void,
  externalInputRef?: RefObject<HTMLInputElement>
): IUseGetGlobalSearchResultsPresenterResult => {
  const { query, submittedQuery, setSubmittedQuery, setQuery, inputRef } =
    useGlobalSearchQueryState(externalInputRef)

  const { navigate } = useGlobalSearchNavigation()
  const { recentItems } = useQueryGlobalSearchRecentItems()
  const { addRecentItem } = useMutateAddGlobalSearchRecentItem()
  const {
    data: searchResult,
    isLoading: isSearchLoading,
    isError: isSearchError,
    error: searchError,
  } = useQueryGlobalSearchResults(submittedQuery)

  const {
    selectedTab,
    selectTab: setTab,
    filteredResultItems,
    tabs,
    resetTab,
  } = useGlobalSearchTabState(searchResult)

  const { openRecentItem, openResultItem } = useGlobalSearchItemActions({
    onClose,
    navigate,
    addRecentItem,
  })

  const submitQuery = useCallback(() => {
    setSubmittedQuery((prev) => (prev === query ? prev : query))
    resetTab()
  }, [query, setSubmittedQuery, resetTab])

  const contentState: Nullable<ContentStateEnum> = deriveContentState({
    hasSubmittedQuery: submittedQuery !== '',
    hasRecentItems: recentItems.length > 0,
    isSearchLoading,
    hasResultItems: (searchResult?.items.length ?? 0) > 0,
    isSearchSuccess:
      !isSearchLoading && !isSearchError && searchResult !== undefined,
    isUnsupportedQuery: searchResult?.isUnsupportedQuery ?? false,
    isSearchError,
  })

  const {
    highlightedIndex,
    isExpanded,
    activeDescendantId,
    navigateDown,
    navigateUp,
    openHighlightedItem,
    resetHighlight,
  } = useGlobalSearchKeyboardNavigation({
    query,
    recentItems,
    filteredResultItems,
    contentState,
    openRecentItem,
    openResultItem,
  })

  const changeQuery = useCallback(
    (value: string) => {
      resetHighlight()
      setQuery(value)
    },
    [resetHighlight, setQuery]
  )

  const selectTab = useCallback(
    (type: GlobalSearchTabTypeEnum) => {
      resetHighlight()
      setTab(type)
    },
    [resetHighlight, setTab]
  )

  const openHighlightedOrSubmit = useCallback(() => {
    if (!openHighlightedItem()) submitQuery()
  }, [openHighlightedItem, submitQuery])

  const showEnterHint =
    query !== submittedQuery && query.length > 0 && highlightedIndex === null

  const showTruncatedNotice = searchResult?.isTruncated ?? false

  const noResultsMessage = searchResult?.noResultsMessage ?? null
  const unsupportedQueryMessage = searchResult?.unsupportedQueryMessage ?? null

  const errorMessage: Nullable<string> =
    searchError instanceof GlobalSearchUseCaseErrors &&
    searchError.name === 'SEARCH_FAILED_WITH_MESSAGE'
      ? searchError.message
      : null

  return {
    commands: {
      changeQuery,
      openHighlightedOrSubmit,
      openRecentItem,
      openResultItem,
      selectTab,
      navigateDown,
      navigateUp,
    },
    data: {
      query,
      submittedQuery,
      recentItems,
      resultItems: filteredResultItems,
      tabs,
      inputRef,
      highlightedIndex,
      activeDescendantId,
      isExpanded,
      originalQuery: searchResult?.originalQuery ?? null,
    },
    states: {
      showEnterHint,
      contentState,
      selectedTab,
      showTruncatedNotice,
      noResultsMessage,
      unsupportedQueryMessage,
      errorMessage,
      showLlmConfirmation: searchResult?.showConversationConfirmation ?? false,
    },
  }
}
